import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Form, Input, Radio, Result, Select, Space, Typography } from "antd";
import { NotificationOutlined, SendOutlined } from "@ant-design/icons";
import { API_BASE } from "./config/api";
import { getDistributorToken, handleDistributorAuthFailure } from "./config/auth";

const { Title, Text } = Typography;

export default function BroadcastNotifications() {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [targetType, setTargetType] = useState("ARN");

  const token = getDistributorToken();
  const isAdmin = useMemo(() => {
    const raw = String(localStorage.getItem("is_admin") || sessionStorage.getItem("is_admin") || "").toLowerCase();
    return raw === "1" || raw === "true" || raw === "yes";
  }, []);

  const sendBroadcast = async (values) => {
    setSending(true);
    setError("");
    setResult(null);

    try {
      const authToken = getDistributorToken();
      if (!authToken) {
        window.location.replace("/login");
        return;
      }

      const body = {
        title: String(values.title || "").trim(),
        message: String(values.message || "").trim(),
        target_type: isAdmin ? targetType : "ARN",
        target_value:
          isAdmin && targetType !== "ALL"
            ? String(values.target_value || "").trim()
            : "",
        deep_link: "/client/dashboard",
      };

      const response = await fetch(`${API_BASE}/distributor_broadcast_send.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-KG-Distributor-Authorization": `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));

      if (typeof handleDistributorAuthFailure === "function" &&
          handleDistributorAuthFailure(response, data)) {
        return;
      }

      if (!response.ok || data.status !== "ok") {
        throw new Error(data.message || "Unable to send notification.");
      }

      setResult(data);
      form.resetFields(["title", "message", "target_value"]);
    } catch (e) {
      setError(e.message || "Unable to send notification.");
    } finally {
      setSending(false);
    }
  };

  if (!token) {
    window.location.replace("/login");
    return null;
  }

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <Card>
        <Space direction="vertical" size={4} style={{ marginBottom: 20 }}>
          <Title level={3} style={{ margin: 0 }}>
            <NotificationOutlined /> Broadcast Notifications
          </Title>
          <Text type="secondary">
            Send a push notification to MFSetu - MyWealth clients.
          </Text>
        </Space>

        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

        {result && (
          <Result
            status={result.delivery_status === "SENT" ? "success" : "warning"}
            title="Broadcast processed"
            subTitle={`Recipients: ${result.recipients || 0} | Sent: ${result.sent || 0} | Failed: ${result.failed || 0}`}
            style={{ padding: "10px 0 20px" }}
          />
        )}

        <Form form={form} layout="vertical" onFinish={sendBroadcast} autoComplete="off">
          {isAdmin ? (
            <>
              <Form.Item label="Target">
                <Radio.Group value={targetType} onChange={(e) => setTargetType(e.target.value)}>
                  <Radio.Button value="ALL">All Clients</Radio.Button>
                  <Radio.Button value="ARN">ARN</Radio.Button>
                  <Radio.Button value="CLIENT">Client User ID</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {targetType === "ARN" && (
                <Form.Item
                  label="ARN Number"
                  name="target_value"
                  rules={[{ required: true, message: "Enter ARN number." }]}
                >
                  <Input size="large" placeholder="Example: ARN-12345" maxLength={100} />
                </Form.Item>
              )}

              {targetType === "CLIENT" && (
                <Form.Item
                  label="Client User ID"
                  name="target_value"
                  rules={[{ required: true, message: "Enter Client User ID." }]}
                >
                  <Input size="large" placeholder="Client User ID" maxLength={100} />
                </Form.Item>
              )}
            </>
          ) : (
            <Alert
              type="info"
              showIcon
              message="This broadcast will be sent only to clients mapped to your authenticated ARN."
              style={{ marginBottom: 18 }}
            />
          )}

          <Form.Item
            label="Notification Title"
            name="title"
            rules={[
              { required: true, message: "Enter notification title." },
              { max: 150, message: "Maximum 150 characters." },
            ]}
          >
            <Input size="large" maxLength={150} showCount placeholder="Notification title" />
          </Form.Item>

          <Form.Item
            label="Message"
            name="message"
            rules={[
              { required: true, message: "Enter notification message." },
              { max: 2000, message: "Maximum 2000 characters." },
            ]}
          >
            <Input.TextArea rows={6} maxLength={2000} showCount placeholder="Type message for your clients..." />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            size="large"
            icon={<SendOutlined />}
            loading={sending}
          >
            Send Broadcast
          </Button>
        </Form>
      </Card>
    </div>
  );
}
